// ============================================================
// site.ts  — サイト全体で使うユーティリティ
// ============================================================

import '../styles/site.scss';
import { hideLoading, showLoading } from './loading';
import './multiSelect';
import './session-keepalive';
import './keypad';
import './select-picker';
import './remember-selection';
import './dialog-dismiss';
import { initializeChoicePickers, initializeNoVehicleLink } from './choicePicker';

initializeChoicePickers();
initializeNoVehicleLink();

// AJAX絞り込み・タブ切替・ソート後の最新条件を、ダウンロード直前に反映する。
document.addEventListener('click', event => {
    const link = (event.target as Element).closest<HTMLAnchorElement>('a.nc-list-download');
    if (!link) return;
    const url = new URL(link.dataset.downloadPath!, location.origin);
    url.search = location.search;
    url.searchParams.delete('page');
    url.searchParams.delete('Page');
    url.searchParams.set('fileType', link.dataset.fileType!);
    link.href = url.toString();
}, true);

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('#mainNavbar .navbar-nav .nav-link.active').forEach(link => link.setAttribute('aria-current', 'page'));
});

/**
 * URL クエリパラメータを取得する
 */
function _getParam(name: string, url?: string): string | null {
    const target = url ?? window.location.href;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp('[?&]' + escaped + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(target);
    if (!results) {
        return null;
    }
    if (!results[2]) {
        return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

// グローバルに公開（Knockout テンプレート等からも参照できるように）
(window as any).getParam = _getParam;

// ============================================================
// フィルタ等の URL 変更を履歴に積むヘルパ
// 短時間 (FILTER_HISTORY_COALESCE_MS) 以内の連続呼び出しは
// replaceState で同一履歴エントリを上書きし、戻る/進む の操作性を保つ。
// ============================================================
const FILTER_HISTORY_COALESCE_MS = 800;
let lastFilterPushAt = 0;

function _pushFilterState(url: string | URL): void {
    const href = typeof url === 'string' ? url : url.href;
    const now = Date.now();
    if (now - lastFilterPushAt < FILTER_HISTORY_COALESCE_MS) {
        window.history.replaceState({}, '', href);
    } else {
        window.history.pushState({}, '', href);
    }
    lastFilterPushAt = now;
}

(window as any).pushFilterState = _pushFilterState;

type NcSortDirection = 'asc' | 'desc';
type NcSortType = 'text' | 'number';

interface NcSortRule {
    key: string;
    col: number;
    type: NcSortType;
    direction: NcSortDirection;
}

function initTableSort(): void {
    const tables = document.querySelectorAll<HTMLTableElement>('table[data-nc-sortable="true"]');
    tables.forEach((table) => initSortableTable(table));
}

function initSortableTable(table: HTMLTableElement): void {
    const tbody = table.tBodies[0];
    if (!tbody) {
        return;
    }

    const headers = Array.from(
        table.querySelectorAll<HTMLTableCellElement>('th[data-sort-key]'),
    ).filter((h) => !!(h.dataset.sortKey && h.dataset.sortKey.trim().length > 0));
    if (headers.length === 0) {
        return;
    }

    // ソート状態の URL パラメータ名 (data-nc-sort-param で上書き可能)
    const sortParam = table.dataset.ncSortParam || 'Sort';
    // サーバ側ソートモード: クライアント側で DOM を並び替えず、URL を更新して
    // popstate を発火させ、ページの既存 refresh フローで再取得させる
    const serverSort = table.dataset.ncSortServer === 'true';

    const sortRulesFromUrl = (): NcSortRule[] => {
        const raw = new URLSearchParams(window.location.search).get(sortParam);
        if (!raw) {
            return [];
        }
        const rules: NcSortRule[] = [];
        raw.split(',').forEach((token) => {
            const [key, dirRaw] = token.split(':');
            if (!key) { return; }
            const header = headers.find((h) => h.dataset.sortKey === key);
            if (!header || header.dataset.sortCol == null) { return; }
            const col = Number(header.dataset.sortCol);
            if (Number.isNaN(col)) { return; }
            const type = header.dataset.sortType === 'number' ? 'number' : 'text';
            const direction: NcSortDirection = dirRaw === 'desc' ? 'desc' : 'asc';
            rules.push({ key, col, type, direction });
        });
        return rules;
    };

    const writeRulesToUrl = (rules: NcSortRule[]): void => {
        const url = new URL(window.location.href);
        if (rules.length === 0) {
            url.searchParams.delete(sortParam);
        } else {
            const value = rules.map((r) => `${r.key}:${r.direction}`).join(',');
            url.searchParams.set(sortParam, value);
        }
        // ソート変更ではページ番号をリセット (1 ページ目に戻す)
        if (url.searchParams.has('Page')) {
            url.searchParams.set('Page', '1');
        }
        if (serverSort) {
            // 戻る/進むで前のソート状態に戻れるよう pushState
            const pushFn = (window as any).pushFilterState as ((u: URL) => void) | undefined;
            if (pushFn) {
                pushFn(url);
            } else {
                window.history.pushState({}, '', `${url.pathname}${url.search}`);
            }
            // ページ側 popstate ハンドラに refresh を促す
            window.dispatchEvent(new PopStateEvent('popstate'));            // refresh AJAX 完了後に KO の text バインディングで thead の
            // textContent が書き換えられても sort マークを検出できるよう、
            // 数回リトライして dataset/class を再付与する
            [50, 250, 600].forEach((delay) => setTimeout(renderSortMarks, delay));        } else {
            window.history.replaceState({}, '', `${url.pathname}${url.search}`);
        }
    };

    let sortRules: NcSortRule[] = sortRulesFromUrl();
    let isApplyingSort = false;
    let nextOriginalOrder = 0;

    const normalizeCellText = (value: string): string => value.replace(/\s+/g, ' ').trim();
    const getRows = (): HTMLTableRowElement[] => Array.from(tbody.querySelectorAll('tr'));

    const ensureOriginalOrder = (): void => {
        getRows().forEach((row) => {
            if (!row.dataset.ncOriginalOrder) {
                row.dataset.ncOriginalOrder = String(nextOriginalOrder++);
            }
        });
    };

    const parseNumber = (value: string): number => {
        const normalized = value.replace(/,/g, '');
        const match = normalized.match(/-?\d+(?:\.\d+)?/);
        return match ? Number(match[0]) : Number.NaN;
    };

    const getCellText = (row: HTMLTableRowElement, col: number): string => {
        const cell = row.cells.item(col);
        if (!cell) {
            return '';
        }
        const customValue = cell.getAttribute('data-sort-value');
        return normalizeCellText(customValue ?? cell.textContent ?? '');
    };

    const compareByRule = (a: HTMLTableRowElement, b: HTMLTableRowElement, rule: NcSortRule): number => {
        const av = getCellText(a, rule.col);
        const bv = getCellText(b, rule.col);

        let result = 0;
        if (rule.type === 'number') {
            const an = parseNumber(av);
            const bn = parseNumber(bv);
            if (!Number.isNaN(an) && !Number.isNaN(bn)) {
                result = an - bn;
            } else {
                result = av.localeCompare(bv, 'ja');
            }
        } else {
            result = av.localeCompare(bv, 'ja');
        }

        return rule.direction === 'asc' ? result : -result;
    };

    const renderSortMarks = (): void => {
        headers.forEach((header) => {
            header.classList.add('nc-sortable-header');
            header.classList.remove('nc-sort-asc', 'nc-sort-desc');
            delete header.dataset.sortOrder;
        });

        sortRules.forEach((rule, index) => {
            const target = headers.find((h) => h.dataset.sortKey === rule.key);
            if (!target) {
                return;
            }
            target.classList.add(rule.direction === 'asc' ? 'nc-sort-asc' : 'nc-sort-desc');
            target.dataset.sortOrder = String(index + 1);
        });
    };

    const reorderRows = (rows: HTMLTableRowElement[]): void => {
        const fragment = document.createDocumentFragment();
        rows.forEach((row) => fragment.appendChild(row));
        isApplyingSort = true;
        try {
            tbody.appendChild(fragment);
        } finally {
            // 自身の DOM 変更で observer が発火して無限ループするのを防ぐため、
            // 記録済みの mutation をクリアしてからフラグを下ろす
            observer.takeRecords();
            isApplyingSort = false;
        }
    };

    const applySort = (): void => {
        if (serverSort) {
            // サーバ側で並んだ結果をそのまま使う
            renderSortMarks();
            return;
        }

        ensureOriginalOrder();

        const rows = getRows();
        if (rows.length <= 1) {
            renderSortMarks();
            return;
        }

        const rowsWithIndex = rows.map((row, index) => ({ row, index }));
        rowsWithIndex.sort((a, b) => {
            for (const rule of sortRules) {
                const result = compareByRule(a.row, b.row, rule);
                if (result !== 0) {
                    return result;
                }
            }

            if (sortRules.length === 0) {
                const ao = Number(a.row.dataset.ncOriginalOrder ?? a.index);
                const bo = Number(b.row.dataset.ncOriginalOrder ?? b.index);
                return ao - bo;
            }

            return a.index - b.index;
        });

        reorderRows(rowsWithIndex.map((x) => x.row));
        renderSortMarks();
    };

    const updateRule = (clickedHeader: HTMLTableCellElement): void => {
        const key = clickedHeader.dataset.sortKey;
        const colRaw = clickedHeader.dataset.sortCol;
        if (!key || colRaw == null) {
            return;
        }

        const col = Number(colRaw);
        if (Number.isNaN(col)) {
            return;
        }
        const type = clickedHeader.dataset.sortType === 'number' ? 'number' : 'text';

        // クリック=常にサイクル(asc → desc → 解除)。
        // 既存ルールはそのまま残し、複合ソートをデフォルトにする。
        const currentIndex = sortRules.findIndex((r) => r.key === key);
        if (currentIndex < 0) {
            sortRules.push({
                key,
                col,
                type,
                direction: 'asc',
            });
        } else if (sortRules[currentIndex].direction === 'asc') {
            sortRules[currentIndex] = {
                ...sortRules[currentIndex],
                direction: 'desc',
            };
        } else {
            sortRules.splice(currentIndex, 1);
        }

        applySort();
        writeRulesToUrl(sortRules);
        updateClearButton();
    };

    // ソート解除ボタンを table の直前に動的に挿入する
    let clearButton: HTMLButtonElement | null = null;
    const ensureClearButton = (): void => {
        if (clearButton) {
            return;
        }
        clearButton = document.createElement('button');
        clearButton.type = 'button';
        clearButton.className = 'btn btn-sm btn-secondary nc-sort-clear-btn mb-1';
        clearButton.innerHTML = '<i class="fa-solid fa-arrow-down-up-across-line me-1" aria-hidden="true"></i>ソート解除';
        clearButton.style.display = 'none';
        clearButton.addEventListener('click', (event) => {
            event.preventDefault();
            sortRules = [];
            applySort();
            writeRulesToUrl(sortRules);
            updateClearButton();
        });
        table.parentElement?.insertBefore(clearButton, table);
    };

    const updateClearButton = (): void => {
        if (!clearButton) {
            return;
        }
        clearButton.style.display = sortRules.length > 0 ? '' : 'none';
    };

    ensureClearButton();

    headers.forEach((header) => {
        header.classList.add('nc-sortable-header');
        header.title = 'クリックで並び替え (複数列クリックで複合ソート)';
        header.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            if (target.closest('.btn,a,button,input,select,textarea,label')) {
                return;
            }
            updateRule(header);
        });
    });

    const observer = new MutationObserver(() => {
        if (isApplyingSort) {
            return;
        }

        ensureOriginalOrder();
        if (sortRules.length > 0) {
            applySort();
        }
    });
    // KO foreach による tbody 再描画でもソートを再適用するため、
    // すべての tbody で observer を有効化。
    // 自身の並べ替えによる無限ループは reorderRows() 内で
    // observer.takeRecords() して防いだケースで代替。
    if (!serverSort) {
        observer.observe(tbody, { childList: true });
    }

    ensureOriginalOrder();
    if (sortRules.length > 0) {
        // URL に Sort パラメータが有る場合、初期ソートを適用
        // (KO foreach の初回レンダリングより後に observer 経由でも走る)
        applySort();
    }
    renderSortMarks();
    updateClearButton();
}

// ============================================================
// テーマ (light/dark) とフォントサイズ (sm/md/lg) の切替
// ============================================================
type NcTheme = 'light' | 'dark';
type NcFontSize = 'sm' | 'md' | 'lg' | 'xl';

function getStoredTheme(): NcTheme {
    const v = localStorage.getItem('nc-theme');
    return v === 'dark' ? 'dark' : 'light';
}

function getStoredFontSize(): NcFontSize {
    const v = localStorage.getItem('nc-font-size');
    return v === 'sm' || v === 'lg' || v === 'xl' ? v : 'md';
}

function applyTheme(theme: NcTheme) {
    document.documentElement.setAttribute('data-bs-theme', theme);
    try {
        localStorage.setItem('nc-theme', theme);
    } catch (e) {
        /* noop */
    }
    updateThemeButton(theme);
}

function applyFontSize(size: NcFontSize) {
    document.documentElement.setAttribute('data-nc-font-size', size);
    try {
        localStorage.setItem('nc-font-size', size);
    } catch (e) {
        /* noop */
    }
    updateFontSizeButtons(size);
}

function updateThemeButton(theme: NcTheme) {
    const $btn = $('#themeToggleButton');
    if ($btn.length === 0) {
        return;
    }
    // ダーク時は太陽アイコン（クリックでライトへ）、ライト時は月アイコン（クリックでダークへ）
    const iconClass = theme === 'dark' ? 'fa-sun' : 'fa-moon';
    $btn.html(`<i class="fa-solid ${iconClass}" aria-hidden="true"></i>`);
    $btn.attr(
        'title',
        theme === 'dark' ? 'ライトモードに切替' : 'ダークモードに切替',
    );
}

function updateFontSizeButtons(size: NcFontSize) {
    $('.nc-font-size-btn').each(function () {
        const $b = $(this);
        const v = $b.attr('data-nc-font-size');
        if (v === size) {
            $b.removeClass('btn-outline-light').addClass('btn-light active');
            $b.attr('aria-pressed', 'true');
        } else {
            $b.removeClass('btn-light active').addClass('btn-outline-light');
            $b.attr('aria-pressed', 'false');
        }
    });
}

$(function () {
    initTableSort();

    // Bootstrap Tooltip: delegation で動的要素 (KO foreach) にも自動適用
    const bs = (window as any).bootstrap;
    if (bs && bs.Tooltip) {
        new bs.Tooltip(document.body, {
            selector: '[data-bs-toggle="tooltip"]',
            container: 'body',
        });
    }

    // 初期状態を画面に反映 (localStorage 値はレイアウトの head で適用済み)
    updateThemeButton(getStoredTheme());
    updateFontSizeButtons(getStoredFontSize());

    $(document).on('click', '#themeToggleButton', function () {
        const next: NcTheme = getStoredTheme() === 'dark' ? 'light' : 'dark';
        applyTheme(next);
    });

    $(document).on('click', '.nc-font-size-btn', function (event) {
        const v = $(event.currentTarget).attr('data-nc-font-size');
        if (v === 'sm' || v === 'md' || v === 'lg' || v === 'xl') {
            applyFontSize(v);
        }
    });
});

// 画面全体ローディング制御
//
// フィルタ/ソート変更や自動更新でのリロード時に、
// Ajax の適用 (= ko.mapping / チャート再描画 など .done() 内の後処理) が
// 完了するまでフルスクリーンオーバーレイを表示する。
//
// 実装メモ:
// - jQuery の `ajaxStart`/`ajaxStop` はリクエスト abort + 即時再送出
//   (Vehicle/Index.ts で連打時に行っている inflightListXhr.abort()) の
//   合間に一瞬カウンタが 0 になり ajaxStop が発火 → オーバーレイがちらつく
//   問題があるため、`ajaxSend`/`ajaxComplete` を独自カウンタで集計する。
// - 非表示は次のマクロタスクまで遅延させ、abort 直後の再送出でも
//   オーバーレイが消えないようにする。これにより `.done()` 内の
//   `setTimeout(0)` で予約された後処理 (ko.mapping deferred /
//   ソートマーク再付与等) の間もオーバーレイが残る。
const PROCESSING_HIDE_DELAY_MS = 120;

$(function () {
    let inflightRequestCount = 0;
    let hideTimerId: number | null = null;

    let shown = false;

    function showProcessing(): void {
        if (hideTimerId !== null) {
            window.clearTimeout(hideTimerId);
            hideTimerId = null;
        }
        if (shown) { return; }
        shown = true;
        showLoading('読み込み中...');
    }

    function hideProcessing(): void {
        if (!shown) { return; }
        shown = false;
        hideLoading();
    }

    function scheduleHideProcessing(): void {
        if (hideTimerId !== null) {
            window.clearTimeout(hideTimerId);
        }
        hideTimerId = window.setTimeout(function () {
            hideTimerId = null;
            if (inflightRequestCount === 0) {
                hideProcessing();
            }
        }, PROCESSING_HIDE_DELAY_MS);
    }

    $(document).ajaxSend(function () {
        inflightRequestCount++;
        showProcessing();
    });

    $(document).ajaxComplete(function () {
        inflightRequestCount = Math.max(0, inflightRequestCount - 1);
        if (inflightRequestCount === 0) {
            scheduleHideProcessing();
        }
    });

    window.addEventListener('beforeunload', function () {
        showProcessing();
    });

    $('#logoutButton').on('click touch', function () {
        showProcessing();
        $.ajax({
            async: false,
            url: '/api/auth/logout',
            type: 'get',
            dataType: 'json',
        })
            .done(function () {
                document.location.href = '/';
            })
            .fail(function () {
                hideProcessing();
                bootbox.alert('ログアウトに失敗しました', '');
            });
    });
});
// Measure natural item widths so permissions, site names and font size all affect layout.
$(function () {
    const nav = document.getElementById('mainNavbar');
    const container = nav?.querySelector<HTMLElement>(':scope > .container-fluid');
    const menu = nav?.querySelector<HTMLElement>('#Navbar > .navbar-nav');
    const actions = nav?.querySelector<HTMLElement>('#Navbar > .d-flex');
    const brand = nav?.querySelector<HTMLElement>('.navbar-brand');
    if (!nav || !container || !menu || !actions || !brand) return;
    let pending = false;
    const update = () => {
        pending = false;
        const style = getComputedStyle(container);
        const available = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const itemsWidth = Array.from(menu.children).reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
        const required = itemsWidth + brand.getBoundingClientRect().width + parseFloat(getComputedStyle(brand).marginRight) + actions.getBoundingClientRect().width;
        nav.classList.toggle('nc-nav-stacked', window.innerWidth >= 576 && required > available);
    };
    const schedule = () => { if (!pending) { pending = true; requestAnimationFrame(update); } };
    const observer = new ResizeObserver(schedule);
    [container, brand, actions, ...Array.from(menu.children)].forEach(element => observer.observe(element));
    window.addEventListener('resize', schedule);
    document.fonts.ready.then(schedule);
    update();
});

// Confirm deletion before submitting the existing antiforgery-protected form.
const confirmedDeleteForms = new WeakSet<HTMLFormElement>();
let deleteConfirmationPending = false;
document.addEventListener('submit', event => {
    const form = event.target as HTMLFormElement;
    if (!form.matches('form[data-confirm-delete]')) return;
    if (confirmedDeleteForms.has(form)) { confirmedDeleteForms.delete(form); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (deleteConfirmationPending) return;
    deleteConfirmationPending = true;
    const message = document.createElement('div');
    const id = form.querySelector<HTMLInputElement>('[name="id"]')?.value;
    message.textContent = form.dataset.confirmDelete + (id ? `（ID：${id}）` : '');
    bootbox.confirm({
        title: '削除の確認',
        message: message.innerHTML,
        buttons: { cancel: { label: 'キャンセル', className: 'btn-secondary' }, confirm: { label: '削除', className: 'btn-danger' } },
        callback: confirmed => {
            deleteConfirmationPending = false;
            if (!confirmed || !form.isConnected) return;
            confirmedDeleteForms.add(form);
            form.requestSubmit();
        }
    });
}, true);

// Both history screens use an explicit desired state; a retry never toggles twice.
document.addEventListener('change', event => {
    const input = event.target as HTMLInputElement;
    if (!input.matches('[data-master-include-hidden]')) return;
    document.cookie = `masterIncludeHidden=${input.checked};path=/;SameSite=Lax`;
    location.reload();
});
document.addEventListener('click', event => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-master-visibility]');
    if (!button || button.disabled) return;
    const hidden = button.dataset.hidden === 'true';
    bootbox.confirm({message: hidden ? 'このマスタを非表示にしますか？' : 'このマスタを再表示しますか？', callback: async confirmed => {
        if (!confirmed) return;
        button.disabled = true;
        const body = new URLSearchParams({kind:button.dataset.masterVisibility!,id:button.dataset.id!,hidden:String(hidden),__RequestVerificationToken:(document.querySelector('[name="__RequestVerificationToken"]') as HTMLInputElement).value});
        try { const result = await fetch('/MasterVisibility/Set',{method:'POST',body});if(!result.ok)throw new Error();location.reload(); }
        catch { button.disabled=false;bootbox.alert('変更できませんでした。非表示を含む重複や参照先を確認してください。'); }
    }});
});
// 編集列を無効化列の左隣に貼り付けるため、無効化列の実幅を CSS 変数で渡す。
// 文字サイズ切替や列の表示切替で幅が変わるので都度測り直す。
function syncHistoryStickyWidth(scope: HTMLElement): void {
    const header = scope.querySelector<HTMLElement>('thead .nc-history-action-column');
    const width = header && header.offsetParent !== null ? header.getBoundingClientRect().width : 0;
    scope.style.setProperty('--nc-history-action-width', `${Math.round(width)}px`);
}
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll<HTMLElement>('[data-history-actions-scope]').forEach(scope => {
        const header = scope.querySelector<HTMLElement>('thead .nc-history-action-column');
        if (header && 'ResizeObserver' in window) {
            new ResizeObserver(() => syncHistoryStickyWidth(scope)).observe(header);
        }
        syncHistoryStickyWidth(scope);
    });
});

// 無効化・編集表示は一覧カード見出しのスライドスイッチで切り替える。
document.addEventListener('change', event => {
    const toggle = (event.target as Element).closest<HTMLInputElement>('input[data-history-actions-enable]');
    if (!toggle) return;
    const scope = toggle.closest<HTMLElement>('[data-history-actions-scope]');
    if (!scope) return;
    scope.classList.toggle('nc-history-actions-enabled', toggle.checked);
    syncHistoryStickyWidth(scope);
});
let historyActionPending = false;
document.addEventListener('click', async (event) => {
 const button=(event.target as Element).closest<HTMLButtonElement>('button.nc-history-toggle');
 if(!button)return;
 event.preventDefault(); event.stopPropagation();
 if(historyActionPending || button.disabled)return;
 const id=button.dataset.historyId!;
 if(!/^[0-9]+$/.test(id))return;
 const desired=button.dataset.disabled==='true';
 const passage=button.dataset.passage==='true';
 historyActionPending=true; button.disabled=true;
 const confirmed=await new Promise<boolean>(resolve => bootbox.confirm({
     title:desired?'履歴の無効化':'履歴の無効解除',
     message:`${passage?'チェックイン履歴':'計量履歴'}（ID：${id}）を${desired?'無効化':'無効解除'}しますか？` +
         (desired?'無効化した履歴は通常表示・ダウンロード・集計の対象から除外されます。':'解除後は表示・集計の対象に戻ります。'),
     buttons:{cancel:{label:'キャンセル',className:'btn-secondary'},confirm:{label:desired?'無効化':'無効解除',className:desired?'btn-danger':'btn-warning'}},
     callback:result=>resolve(result)
 }));
 if(!confirmed){historyActionPending=false;button.disabled=false;return;}
 const body=new URLSearchParams({id,passage:String(passage),disabled:String(desired),__RequestVerificationToken:(document.querySelector('[name="__RequestVerificationToken"]') as HTMLInputElement).value});
 try { const response=await fetch('/HistoryControl/Toggle',{method:'POST',body}); if(!response.ok)throw new Error(); location.reload(); }
 catch { alert('無効状態を保存できませんでした。保存済みの状態を再読み込みします。'); location.reload(); }
});

document.addEventListener('change', event => {
    const toggle=(event.target as Element).closest<HTMLInputElement>('input[data-history-edit-enable]');
    if(!toggle)return;
    const scope=toggle.closest<HTMLElement>('[data-history-actions-scope]');
    if(!scope)return;
    scope.classList.toggle('nc-history-edit-enabled', toggle.checked);
    syncHistoryStickyWidth(scope);
});
document.addEventListener('submit', async event => {
    const form=event.target as HTMLFormElement;
    if(form.id!=='metric-edit')return;
    event.preventDefault();
    const button=form.querySelector<HTMLButtonElement>('[type="submit"]')!;
    if(button.disabled)return;
    button.disabled=true;
    const confirmed=await new Promise<boolean>(resolve=>bootbox.confirm({message:'修正版を登録し、元の計量履歴を無効化しますか？',callback:resolve}));
    if(!confirmed){button.disabled=false;return;}
    const message=document.getElementById('metric-edit-message')!;
    const body=new URLSearchParams();new FormData(form).forEach((value,key)=>body.append(key,String(value)));
    try{
        const response=await fetch('/MetricEdit/Submit',{method:'POST',body});
        if(!response.ok)throw new Error(response.status===403?'編集権限がありません。':await response.text());
        const dialog=form.closest<HTMLElement>('.modal');
        if(dialog){
            // モーダルから開いた場合は閉じて一覧を取り直す。
            (window as any).bootstrap?.Modal.getInstance(dialog)?.hide();
            bootbox.alert({title:'計量履歴の編集',message:'編集を受け付けました。展開処理の完了後、計量履歴に反映されます。',
                buttons:{ok:{label:'閉じる',className:'btn-primary'}}});
            const refresh=document.querySelector<HTMLElement>('[data-bind*="click:refresh"]');
            if(refresh)refresh.click(); else location.reload();
            return;
        }
        message.textContent='編集を受け付けました。展開処理の完了後、計量履歴に反映されます。';
        form.querySelectorAll<HTMLInputElement|HTMLSelectElement>('input,select').forEach(input=>input.disabled=true);
    }catch(error){message.textContent=error instanceof Error?error.message:'受付を確認できませんでした。同じ内容で再送してください。';button.disabled=false;}
});
